import { FC, ReactNode } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { beautifyEnum } from 'src/utils/utility';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconInfo from 'src/assets/images/icons/information-outline-grey.svg';
import { NoContractForm, GroupedShippingService } from './types';

interface ITableProps {
  data: GroupedShippingService[];
  onSelectGroup?: (group: GroupedShippingService) => void;
}

export interface ITableCellProps {
  renderedCellValue: ReactNode;
  children?: ReactNode;
  // eslint-disable-next-line react/no-unused-prop-types
  fieldName?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  row?: MRT_Row<GroupedShippingService>;
  isMismatched?: boolean;
}

const TableCell = ({ renderedCellValue, isMismatched, children }: ITableCellProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      color: isMismatched ? 'red' : '',
    }}
  >
    {children || renderedCellValue}
  </Box>
);

const useCompareColumns = (): MRT_ColumnDef<GroupedShippingService>[] => [
  {
    enableEditing: false,
    accessorKey: 'serviceProvider',
    header: 'Trade Lane',
    accessorFn: (row) => {
      // Get listZone from first variant
      const listZone = row?.variants?.[0]?.listZone;
      return (
        <div className='flex items-center gap-10 min-w-[140px]'>
          <div className='relative'>{row?.route}</div>
          {row?.modality === 'COURIER' && listZone && listZone.length > 0 && (
            <CustomTooltip
              title={
                <div className='grid grid-cols-2 gap-8'>
                  {listZone
                    ?.slice()
                    ?.sort((a, b) => {
                      if (a.pol?.localeCompare(b.pol) !== 0) {
                        return a.pol?.localeCompare(b.pol);
                      }
                      return a.pod?.localeCompare(b.pod);
                    })
                    ?.map((item) => (
                      <div key={`${item?.pol}_${item?.pod}`} className='py-8 border-b border-neutral-70'>
                        {item?.pol} / {item?.pod}
                      </div>
                    ))}
                </div>
              }
            >
              <img width={20} src={IconInfo} alt='' />
            </CustomTooltip>
          )}
        </div>
      );
    },
    Cell: ({ renderedCellValue, row }) => {
      // Check service-level mismatches (not contract-level)
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('route') || serviceMismatchedFields.includes('pol') || serviceMismatchedFields.includes('pod');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    enableEditing: false,
    accessorKey: 'shipperName',
    header: 'Shipper Name',
    accessorFn: (row) => <>{row?.shipperName ? beautifyEnum(row.shipperName).toUpperCase() : '-'}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('shipper name') || serviceMismatchedFields.includes('shipperName');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    enableEditing: false,
    accessorKey: 'consigneeName',
    header: 'Consignee Name',
    accessorFn: (row) => <>{row?.consigneeName ? beautifyEnum(row.consigneeName).toUpperCase() : '-'}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('consignee name') || serviceMismatchedFields.includes('consigneeName');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    header: 'Billed Entity',
    enableEditing: false,
    accessorKey: 'billingPartyCode',
    accessorFn: (row) => <>{row?.billingPartyCode ? beautifyEnum(row.billingPartyCode).toUpperCase() : '-'}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched =
        serviceMismatchedFields.includes('billingPartyCode') ||
        serviceMismatchedFields.includes('billing party code') ||
        serviceMismatchedFields.includes('billed entity');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    header: 'Service',
    enableEditing: false,
    accessorKey: 'service',
    accessorFn: (row) => <>{row?.service}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('service');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    header: 'Type',
    enableEditing: false,
    accessorKey: 'type',
    accessorFn: (row) => <>{row?.type}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('type');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    enableEditing: false,
    accessorKey: 'modality',
    header: 'Modality',
    accessorFn: (row) => <>{row?.modality}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('modality');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    header: 'Carrier',
    enableEditing: false,
    accessorKey: 'carrier',
    accessorFn: (row) => <>{row?.carrier}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('carrier');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
  {
    header: 'Incoterm',
    enableEditing: false,
    accessorKey: 'incoterm',
    accessorFn: (row) => <>{row?.incoterm}</>,
    Cell: ({ renderedCellValue, row }) => {
      const serviceMismatchedFields = row.original.serviceMismatchedFields || [];
      const isMismatched = serviceMismatchedFields.includes('incoterm');
      return <TableCell renderedCellValue={renderedCellValue} isMismatched={isMismatched} />;
    },
  },
];

export const TableShippingService: FC<ITableProps> = (props) => {
  const { data = [], onSelectGroup } = props;

  const { watch } = useFormContext<NoContractForm>();
  const columns = useCompareColumns();
  const { contractChoice } = watch();

  const table = useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    enableRowSelection: false,
    enableMultiRowSelection: false,
    displayColumnDefOptions: {
      'mrt-row-select': {
        enableHiding: true,
        Header: '',
      },
    },
    data,
    columns,
    muiTableBodyRowProps: ({ row }) => ({
      selected: row.original.variants.some((v) => v.contractFlowId === contractChoice),
      onClick: () => {
        if (onSelectGroup) {
          onSelectGroup(row.original);
        }
      },
      sx: {
        cursor: 'pointer',
        '&.Mui-selected .MuiTableCell-root': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
        '&:hover': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
        '&.Mui-selected:hover .MuiTableCell-root': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
      },
    }),
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
  });

  return <CustomTable table={table} />;
};
